<template>
  <base-container>
    <img
      :src="
        city && city.donationSectionImage && city.donationSectionImage.original
          ? getImage(city.donationSectionImage.original)
          : city && city.partner && city.partner.image && city.partner.image.original
          ? getImage(city.partner.image.original)
          : require('@/assets/donation.png')
      "
      alt=""
    />
    <h1 id="donation-cart-total" class="text-center text-lg font-weight-bold">
      {{ $t('views.donation.cartTotal', [$n(totalWithDonation(), 'currency')]) }}
    </h1>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <h2 v-if="city && city.donationSectionTitle" class="mt-4 text-center text-lg font-weight-bold h1" v-html="city.donationSectionTitle" />
    <h2 v-else class="mt-4 text-center text-lg font-weight-bold h1">{{ $t('views.donation.wouldYouLike') }}</h2>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <h2 v-if="city && city.donationSectionDescription" class="text-dark h3" v-html="city.donationSectionDescription" />
    <h2 v-else-if="city && city.safeAndHappyFund" class="text-dark h3">
      {{ $t('views.donation.netProceeds', [city.safeAndHappyFund, city.name]) }}
    </h2>

    <section id="donation-select" class="px-2 mt-4 mb-4">
      <label class="mb-0">{{ $t('views.donation.selectAmount') }}</label>
      <b-row>
        <b-col v-if="roundUpDonationEnabled" cols="12">
          <b-button
            v-if="roundUpDonationAmount"
            class="mt-3 donation-option d-flex text-left align-items-center"
            :variant="donationButton && donationButtonAmount === roundUpDonationAmount && roundUpDonation ? 'primary' : 'outline-primary'"
            block
            @click="setButtonDonation(roundUpDonationAmount, true)"
          >
            <div class="d-flex align-items-stretch">
              <fa-icon :icon="['fas', 'ribbon']" class="ml-2 mr-3 text-warning" size="2x" />
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div
              v-html="$t('views.donation.roundUpDonation', [$n(roundedUpDonationTotal, 'currency'), $n(roundUpDonationAmount, 'currency')])"
            />
          </b-button>
        </b-col>
        <b-col v-for="amount in donationAmounts" :key="amount" cols="6" sm="4">
          <b-button
            v-if="amount"
            class="mt-3 donation-option"
            :variant="donationButton && donationButtonAmount === amount && !roundUpDonation ? 'primary' : 'outline-primary'"
            block
            @click="setButtonDonation(amount)"
          >
            <fa-icon :icon="['far', 'dollar-sign']" class="icon p-0" />
            {{ $n(amount, 'decimal') }}
          </b-button>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col cols="12" sm="7">
          <ValidationProvider v-slot="{ errors, dirty, valid, validated }" ref="donationValidator" rules="minMaxNumeric:1,100000">
            <b-form-group>
              <b-input-group class="mb-n4 mt-10" size="md">
                <b-input-group-prepend is-text class="custom-prepend">
                  <fa-icon :icon="['far', 'dollar-sign']" class="sm dollar-sign" />
                </b-input-group-prepend>

                <b-form-input
                  id="donation-input"
                  v-model="donationAmount"
                  :state="dirty && validated ? valid : null"
                  aria-describedby="donation-amount-feedback"
                  placeholder="Custom Amount"
                  @input="customDonationChange"
                />
                <b-form-invalid-feedback id="donation-amount-feedback">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-input-group>
            </b-form-group>
          </ValidationProvider>
        </b-col>
        <b-col></b-col>
      </b-row>
    </section>

    <b-button
      type="submit"
      class="mt-3"
      variant="primary"
      block
      :disabled="donationSelected || donationLoading || hasValidationErrors"
      @click="checkoutClick"
    >
      {{ $t('views.donation.continue') }}
      <div v-if="donationLoading" role="status" class="fa-pulse float-right mt-1">
        <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
        <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
      </div>
    </b-button>
    <b-button
      type="submit"
      class="mt-3"
      :variant="!donationSelected ? 'primary' : 'outline-primary'"
      block
      :disabled="!donationSelected || donationLoading || hasValidationErrors"
      @click="skipClick"
    >
      {{ $t('views.donation.skip') }}
    </b-button>
  </base-container>
</template>

<script>
import { mapState } from 'vuex';
import { getImage } from '@/utils';
import BaseContainer from '@/components/BaseContainer';
import { ValidationProvider } from 'vee-validate';

export default {
  components: {
    BaseContainer,
    ValidationProvider,
  },
  data() {
    return {
      donationAmount: null,
      donationButton: false,
      donationButtonAmount: null,
      roundUpDonation: false,
      validationErrors: false,
      hasValidationErrors: false,
    };
  },
  computed: {
    ...mapState({
      donationLoading: (state) => state.order.donation.loading,
      order: (state) => state.order.order,
      city: (state) => (state.order.order ? state.order.order.city : null),
    }),
    roundUpDonationAmount() {
      return Number(this.roundedUpDonationTotal - this.order.total);
    },
    roundedUpDonationTotal() {
      return Number(this.order.roundUpAmount);
    },
    donationSelected() {
      return !this.donationAmount && !this.donationButtonAmount;
    },
    roundUpDonationEnabled() {
      return this.city?.roundUpMyOrder;
    },
    donationAmounts() {
      return this.city?.useCustomDonationAmounts ? this.city.donationAmounts.map((donation) => donation.amount) : [2, 5, 10, 25, 50, 100];
    },
  },
  watch: {
    donationAmount: {
      immediate: true,
      handler(newVal) {
        if (newVal < 0) {
          this.donationAmount = null;
        }
        
        this.$nextTick(async () => {
          if (this.$refs.donationValidator) {
            await this.$refs.donationValidator.validate();
            this.validationErrors = this.$refs.donationValidator.errors.length > 0;
          }
        });
      }
    },
    validationErrors: {
      handler(newVal) {
        this.hasValidationErrors = newVal;
      },
      immediate: true,
    },
  },
  created() {
    if (!this.order.id) {
      this.$router.push({ name: 'Basket' });
    } else if (this.city && !this.city?.partner?.product?.id) {
      if (this.order.items.length > 0) {
        this.$router.push({ name: 'Payment' });
      } else {
        this.$router.push({ name: 'Basket' });
      }
    }
  },
  methods: {
    getImage,
    totalWithDonation() {
      let total = this.order.total;
      if (!this.validationErrors && this.donationAmount) {
        total += parseFloat(this.donationAmount);
      } else if (this.donationButtonAmount) {
        total += this.donationButtonAmount;
      }
      return parseFloat(total);
    },
    setButtonDonation(amount, roundUpDonation = false) {
      if (this.donationButton && this.donationButtonAmount === amount) {
        this.donationButton = false;
        this.donationButtonAmount = null;
      } else {
        this.donationButton = true;
        this.donationButtonAmount = amount;
        this.donationAmount = null;
      }
      this.roundUpDonation = roundUpDonation;
    },
    customDonationChange() {
      this.donationButton = false;
      this.donationButtonAmount = null;
    },
    checkoutClick() {
      const amount = this.donationButton ? this.donationButtonAmount : this.donationAmount;
      const route = { name: 'Payment' };

      if (amount) {
        this.$store.dispatch('order/orderDonation', { amount, push: () => this.$router.push(route) });
      } else {
        this.$router.push(route);
      }
    },
    skipClick() {
      this.$router.push({ name: 'Payment' });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
img {
  margin-left: -1.5rem;
  width: calc(100% + 3rem);
  margin-top: -1.9rem;
}

@media screen and (min-width: 576px) {
  img {
    margin-left: -1.5rem;
    width: calc(100% + 3rem);
    margin-top: -2.5rem;
  }
}

h1 {
  font-size: 1.6rem;
}
.input-group-text {
  background-color: #ffffff;
}

#donation-input::-webkit-outer-spin-button,
#donation-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
#donation-input[type='number'] {
  -moz-appearance: textfield; /* Firefox */
}
.icon {
  position: absolute;
  top: 0;
  left: 0;
  margin-left: 25px;
  margin-top: 33.5px;
}
button.btn-outline-primary.donation-option:not(:hover) {
  background-color: $dp-background;
  color: #495057;
  border: 1px solid $dp-gray-3;
}
#donation-cart-total {
  border-bottom: 2px solid $dp-gray-2;
  margin-left: -1.5rem;
  margin-right: -1.5rem;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

.custom-prepend {
  background-color: #f6f6f6;
  display: flex;
}
</style>
